import { FC, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { APIProvider } from "@vis.gl/react-google-maps";
import { PlaceAutocomplete } from "../../PlaceAutocomplete";
import { GoogleMap } from "../../GoogleMap";
import { CustomerAddressModel } from "../../../models";
import { UpdateCustomerAddress } from "../../../services";
import { extractAddressDetails } from "../../../helpers";
import { useAuth, useStore } from "../../../hooks";
import { Toast } from "../../Toast";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

type Props = {
  address: CustomerAddressModel;
  show: boolean;
  handleSuccess: (data: CustomerAddressModel) => void;
  handleClose: () => void;
};

const EditAddressModal: FC<Props> = ({
  address,
  show,
  handleSuccess,
  handleClose,
}) => {
  const { storeId } = useStore();
  const { customerId } = useAuth();

  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(null);
  const [placeChanged, setPlaceChanged] = useState<boolean>(false);
  const [addressType, setAddressType] = useState<string>(address.address_name);

  const handlePlaceChange = (place: google.maps.places.PlaceResult | null) => {
    setPlaceChanged(true);
    setSelectedPlace(place);
  };

  const handleSubmit = async () => {
    if (!addressType) {
      Toast.error("Please choose an address type");
      return;
    }

    let updatedAddress = { ...address, address_name: addressType };

    if (placeChanged) {
      if (!selectedPlace) {
        Toast.error("Please select a location");
        return;
      }

      const { address_street, city, state, postal_code, latitude, longitude } =
        extractAddressDetails(selectedPlace);
      updatedAddress = {
        id: address.id,
        address_name: addressType,
        address_street,
        city_name: city,
        state_code: state,
        zip_code: postal_code,
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
      };
    }

    const { status, message, data } = await UpdateCustomerAddress({
      store_id: storeId,
      customer_id: customerId,
      address: updatedAddress,
    });

    if (status) {
      Toast.info(message);
      handleSuccess(data);
    } else Toast.error(message);
  };

  const addressTypes = [
    { id: 1, label: "Home" },
    { id: 2, label: "Work" },
    { id: 3, label: "Other" },
  ];

  return (
    <Modal
      className="order_option_popup"
      show={show}
      onHide={handleClose}
      fullscreen="sm-down"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Address</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="Profile_main">
          <div className="order_option_input address_icon mb-3">
            <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
              <PlaceAutocomplete
                onPlaceSelect={handlePlaceChange}
                defaultValue={`${address.address_street}, ${address.city_name}, ${address.state_code}, ${address.zip_code}`}
              />
              <GoogleMap selectedPlace={selectedPlace} />
            </APIProvider>
          </div>

          <Row className="mb-3">
            {addressTypes.map((item) => (
              <Col key={item.id} sm={12}>
                <Form.Check
                  id={`address_type_${item.id}`}
                  label={item.label}
                  name="address_type"
                  type="radio"
                  onChange={() => setAddressType(item.label)}
                  checked={addressType === item.label}
                />
              </Col>
            ))}
          </Row>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" onClick={handleSubmit}>
          Update
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditAddressModal;
